// src/components/PackageListPage.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  ListGroup,
  Form,
  Offcanvas,
  Button,
  Badge,
  Placeholder,
} from 'react-bootstrap';
import { Filter, Search, Users, Calendar } from 'lucide-react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../FireBase';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import '../css/RoomListPage.css'; // ✅ reuse same css
import SearchModal from '../components/SearchModal';
import TourSearchModal from '../components/TourSearchModal';


export default function PackageListPage() {
  const [packages, setPackages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgLoaded, setImgLoaded] = useState({}); // { [id]: true }
  const [categories,setCategories]= useState([]);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showSearchModal, setShowSearchModal] = useState(false);


  // Filters are disabled (custom per-package UI), but category can stay for grouping
  const [selectedCategory, setSelectedCategory] = useState('All Packages');
  const STORAGE_KEY = 'bookingSearch';
  const [range, setRange]= useState({ from: undefined, to: undefined });

  const navigate = useNavigate();
  const guestsList = [
    '1 Adult','2 Adults','3 Adults','4 Adults','Family (2+2)',
  ];
 const [guests, setGuests]  = useState(guestsList[0]);  
  // Hydrate range&guests after modal closes
    useEffect(() => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setShowSearchModal(true);
      }
    }, []);
    
  useEffect(() => {
    if (!showSearchModal) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const { from, to, guests: g } = JSON.parse(saved);
          setRange({
            from: from ? new Date(from) : undefined,
            to:   to   ? new Date(to)   : undefined,
          });
          if (guestsList.includes(g)) setGuests(g);
        } catch {}
      }
    }
  }, [showSearchModal]);
  // Responsive listener
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fetch packages
  useEffect(() => {
    (async () => {
      try {
        const q = query(collection(db, 'packages'), orderBy('name'));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPackages(data);
        setCategories(['All Packages', ...Array.from(new Set(data.map(r => r.name)))]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Categories (Honeymoon/Family/Student/Bachelor)


  // Apply category filter only, as others are no longer needed here
  useEffect(() => {
    let tmp = [...packages];

    if (selectedCategory !== 'All Packages') {
      const val = selectedCategory.toLowerCase();
      tmp = tmp.filter(p => (p.name || '').toLowerCase() === val);
    }

    setFiltered(tmp);
  }, [packages, selectedCategory]);

  // ⬅️ same-looking filter UI as RoomListPage (uses 'search-box', etc.)
  const renderFilters = () => (
    <>
         
      <div
        className="d-flex align-items-center gap-3 px-3 py-2 w-100 mb-2 search-box"
        style={{ maxWidth: '98%', cursor: 'pointer' }}
        onClick={() => { setShowSearchModal(true); setShowOffcanvas(false); }}
      >
        <Search size={17} className="text-secondary" />
        <div>
          <div className="fw-semibold" style={{ fontSize: 13 }}>
            Find your perfect Kodaikanal package
          </div>
          <div className="text-secondary" style={{ fontSize: 13 }}>
            Honeymoon · Family · Student · Bachelor
          </div>
        </div>
      </div>

      <h5 className="mb-3">Categories</h5>
      <ListGroup variant="flush" className="mb-4">
        {categories.map(cat => (
          <ListGroup.Item
            key={cat}
            action
            active={selectedCategory === cat}
            variant={selectedCategory === cat ? 'primary' : 'light'}
            onClick={() => { setSelectedCategory(cat); setShowOffcanvas(false); }}
          >
            {cat}
          </ListGroup.Item>
        ))}
      </ListGroup>
      <p className="text-muted">No additional price/guest/day filters; each package has its own customization options.</p>
    </>
  );

  return (
    <Container fluid className="py-4" style={{ marginBottom: 60 }}>
      <Row>
        {/* Desktop Sidebar (same sticky behavior) */}
        <Col md={3} className="d-none d-md-block" style={{ position: 'sticky', top: 20 }}>
          {renderFilters()}
        </Col>

        {/* Main */}
        <Col xs={12} md={9}>
          {loading ? (
            <Row className="g-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Col key={i} xs={12} md={6}>
                  <Card className="shadow-sm rounded overflow-hidden room-card">
                    <div
                      className="placeholder-glow"
                      style={{ height: 200, backgroundColor: '#e9ecef' }}
                    >
                      <Placeholder xs={12} animation="glow" />
                    </div>
                    <Card.Body>
                      <Placeholder animation="glow">
                        <Placeholder as={Card.Title} xs={8} />
                        <Placeholder xs={6} className="mt-2" />
                        <Placeholder xs={4} className="mt-2" />
                      </Placeholder>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <>
              {/* Mobile Floating Filter Button — same class/feel */}
              {isMobile && (
                <Button
                  className="d-md-none btn-own"
                  onClick={() => setShowOffcanvas(true)}
                  style={{
                    position: 'fixed',
                    bottom: 80,
                    right: 20,
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    zIndex: 1000,
                  }}
                >
                  <Filter size={24} />
                </Button>
              )}

              {/* Packages Grid (cards reuse 'room-card' styles) */}
              <Row className="g-4">
                {filtered.map(pkg => {
                  const cover = Array.isArray(pkg.images) && pkg.images.length > 0 ? pkg.images[0] : '';
                  const loaded = imgLoaded[pkg.id] === true;

                  return (
                    <Col key={pkg.id} xs={12} md={6}>
                      <Card
                        className="shadow-sm rounded overflow-hidden room-card"
                        onClick={() => navigate(`/packages/${pkg.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="position-relative">
                          {!loaded && (
                            <div
                              className="placeholder-glow"
                              style={{ height: 200, backgroundColor: '#e9ecef' }}
                            />
                          )}
                          {cover && (
                            <Card.Img
                              variant="top"
                              src={cover}
                              onLoad={() => setImgLoaded(prev => ({ ...prev, [pkg.id]: true }))}
                              style={{
                                height: 200,
                                objectFit: 'cover',
                                display: loaded ? 'block' : 'none',
                              }}
                              alt={pkg.name}
                            />
                          )}

                          {/* Example badge reusing your badge placement */}
                          {(pkg.category === 'honeymoon' || pkg.category === 'Honeymoon') && (
                            <Badge
                              bg="danger"
                              pill
                              className="position-absolute"
                              style={{ top: 10, right: 10 }}
                            >
                              Honeymoon
                            </Badge>
                          )}
                        </div>

                        <Card.Body>
                          {!loaded ? (
                            <>
                              <Skeleton as={Card.Title} width={200} />
                              <Skeleton width={120} className="mt-2" />
                              <Skeleton width={140} className="mt-2" />
                            </>
                          ) : (
                            <>
                              {(() => {
                                const guestMin = Number(pkg.minMembers ?? pkg.minGuests ?? pkg.minPeople ?? pkg.guestCount ?? 1);
                                const guestMax = Number(pkg.maxMembers ?? pkg.maxGuests ?? pkg.maxPeople ?? pkg.guestCount ?? guestMin);
                                const daysMin = Number(pkg.minDays ?? pkg.days ?? 1);
                                const daysMax = Number(pkg.maxDays ?? pkg.days ?? daysMin);
                                const pricePerson = Number(pkg.pricePerPerson ?? pkg.price ?? 0);

                                return (
                                  <>
                                    <div
                                      className="d-flex justify-content-between align-items-center mb-1"
                                      style={{ color: '#0f172a' }}
                                    >
                                      <b>{pkg.name}</b>
                                      <div className="d-flex align-items-center text-muted">
                                        <Users size={16} className="me-1" />
                                        {guestMin === guestMax ? `${guestMin} Guests` : `${guestMin} - ${guestMax} Guests`}
                                      </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <div className="fw-bold" style={{ color: '#54b435' }}>
                                        ₹{pricePerson.toLocaleString('en-IN')}
                                        <small className="text-muted ms-1">/ person</small>
                                      </div>
                                      <div className="text-muted d-flex align-items-center">
                                        <Calendar size={16} className="me-1" />
                                        {daysMin === daysMax ? `${daysMin} Days` : `${daysMin} - ${daysMax} Days`}
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}

                {filtered.length === 0 && !loading && (
                  <Col xs={12}>
                    <div className="text-center text-muted py-5">
                      No packages match your filters.
                    </div>
                  </Col>
                )}
              </Row>
            </>
          )}
        </Col>
      </Row>

      {/* Mobile Offcanvas — same structure */}
      <Offcanvas show={showOffcanvas} onHide={() => setShowOffcanvas(false)} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filter Packages</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>{renderFilters()}</Offcanvas.Body>
      </Offcanvas>

        
             {/* <TourSearchModal
                                show={showSearchModal}
                                onHide={() => setShowSearchModal(false)}
                                durationDays={''}
                                /> */}
    </Container>
  );
}
